import { Fragment } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ResponseScore } from "../../psyEvaluations.types";
import { FormatedField } from "../../../../types/fields.types";
import { ScoresCell, ScoresCellContent, ScoresColumn, ScoresColumnContext, ScoresHeader } from "./scoresTable.types";
import { buildRowDimensions } from "./scoresTableUtils";
import { calculatedScoreColumn, dimensionNameColumn, isEmphasizedRow, rawScoreColumn } from "./scoresTableColumns";

type PsyEvaluationScoresTableProps = {
	scores: Array<ResponseScore>;
	formatedFields: Array<FormatedField>;
	levelColors: Record<string, string>;
	trailingColumns: Array<ScoresColumn>;
};

export const PsyEvaluationScoresTable = ({ scores, formatedFields, levelColors, trailingColumns }: PsyEvaluationScoresTableProps) => {
	const rowsDimensions = buildRowDimensions(scores);
	const context: ScoresColumnContext = { rowsDimensions, formatedFields, levelColors };
	const scoresColumns: Array<ScoresColumn> = [dimensionNameColumn, rawScoreColumn, calculatedScoreColumn, ...trailingColumns].filter(col => col.isVisible(context))

	return (
		<Grid container justifyContent="flex-start">
			<Grid xs={12} px={4} pt={4} pb={4}>
				<TableContainer component={Paper} sx={{ width: "fit-content" }}>
					<Table size="small" sx={{ width: "auto" }}>
						<TableHead>
							<TableRow>
								{scoresColumns.map((col) => (
									<Fragment key={col.id}>{renderScoresHeader(col.getHeader(context))}</Fragment>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{rowsDimensions.map((row, rowIndex) => (
								<TableRow key={row.scores.id} sx={isEmphasizedRow(row) ? { "& > td": { fontWeight: 600 } } : undefined}>
									{scoresColumns.map((col) => (
										<Fragment key={col.id}>{renderScoresCell(col.getCell(row, rowIndex, context))}</Fragment>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Grid>
		</Grid>
	);
};

const renderScoresCell = (cell: ScoresCell | null) => {
	if (!cell) return null;
	if (cell.content.type === "text") {
		return (
			<TableCell align={cell.align} sx={{ backgroundColor: cell.backgroundColor }}>
				{cell.content.value}
			</TableCell>
		);
	}
	return <LegendCell content={cell.content} rowSpan={cell.rowSpan} />;
};

type LegendCellProps = {
	content: Extract<ScoresCellContent, { type: "legend" }>;
	rowSpan?: number;
};

const LegendCell = ({ content, rowSpan }: LegendCellProps) => {
	return (
		<TableCell rowSpan={rowSpan} sx={{ verticalAlign: "top", maxWidth: { xs: 280, md: 390 } }}>
			<Box display="flex" flexDirection="column">
				{content.entries.map((entry, index) => (
					<Box key={entry.key} display="flex" alignItems={content.entryAlign} marginTop={index !== 0 ? 2 : 1}>
						<Box sx={{ width: 16, height: 16, backgroundColor: entry.swatchColor, border: "1px solid rgba(0,0,0,0.12)", flexShrink: 0, marginTop: "2px" }} />
						<Box marginLeft={1.5}>{entry.rangeText != null ? `${entry.rangeText}: ${entry.description}` : entry.description}</Box>
					</Box>
				))}
			</Box>
		</TableCell>
	);
};

const renderScoresHeader = (header: ScoresHeader) => {
	return (
		<TableCell align={header.align} sx={{ fontWeight: "bold", color: "primary.main" }}>
			{header.label}
		</TableCell>
	);
};
